import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, ListGroup, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import { reportApi } from '../api';
import { useToast } from '../context/ToastContext';
import { formatDate, formatMoney, toInputDate } from '../utils/format';

const PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 6 },
  { label: 'Last 30 days', days: 29 },
  { label: 'Last 90 days', days: 89 },
];

export default function Reports() {
  const toast = useToast();
  const navigate = useNavigate();

  const [from, setFrom] = useState(toInputDate(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(toInputDate(new Date()));
  const [summary, setSummary] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from, to };
      const [s, d, m, f] = await Promise.all([
        reportApi.summary(params),
        reportApi.topDiagnoses(params),
        reportApi.topMedicines(params),
        reportApi.followUps({ days: 14 }),
      ]);
      setSummary(s.data);
      setDiagnoses(d.data);
      setMedicines(m.data);
      setFollowUps(f.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [from, to, toast]);

  useEffect(() => {
    load();
  }, [load]);

  function applyPreset(days) {
    setFrom(toInputDate(new Date(Date.now() - days * 86400000)));
    setTo(toInputDate(new Date()));
  }

  const maxDaily = Math.max(1, ...(summary?.daily || []).map((d) => d.visits));

  return (
    <>
      <PageHeader title="Reports" subtitle="How the clinic has been running" icon="bi-graph-up">
        <Button variant="outline-secondary" onClick={() => window.print()}>
          <i className="bi bi-printer me-1" /> Print
        </Button>
      </PageHeader>

      <Card className="mb-3 no-print">
        <Card.Body className="d-flex flex-wrap align-items-end gap-2">
          <div>
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="d-flex gap-1 flex-wrap ms-md-3">
            {PRESETS.map((p) => (
              <Button key={p.label} size="sm" variant="outline-secondary" onClick={() => applyPreset(p.days)}>
                {p.label}
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      {loading || !summary ? (
        <Loader label="Crunching the numbers..." className="py-5" />
      ) : (
        <>
          <Row className="g-4 mb-4">
            <Col xs={6} lg={3}>
              <StatCard
                label="Total Visits"
                value={summary.totals.visits || 0}
                icon="bi-people-fill"
                color="#2f4bb5"
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="New Patients"
                value={summary.newPatients || 0}
                icon="bi-person-plus-fill"
                color="#d9a213"
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="Collected"
                value={formatMoney(summary.totals.collected || 0)}
                icon="bi-cash-coin"
                color="#1f9d61"
              />
            </Col>
            <Col xs={6} lg={3}>
              <StatCard
                label="Pending Dues"
                value={formatMoney((summary.totals.billed || 0) - (summary.totals.collected || 0))}
                icon="bi-exclamation-circle"
                color="#e0563f"
              />
            </Col>
          </Row>

          <Row className="g-4">
            <Col lg={7}>
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-bar-chart me-2" /> Visits per day
                </Card.Header>
                <Card.Body>
                  {summary.daily.length === 0 ? (
                    <EmptyState icon="bi-calendar-x" title="No visits in this period" />
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: 380 }}>
                      <Table size="sm" className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th style={{ width: '55%' }}>Visits</th>
                            <th className="text-end">New</th>
                            <th className="text-end">Collected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.daily
                            .slice()
                            .reverse()
                            .map((d) => (
                              <tr key={d.date}>
                                <td className="small">{formatDate(d.date)}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      style={{
                                        height: 14,
                                        borderRadius: 4,
                                        background: 'var(--clinic-primary)',
                                        width: `${(d.visits / maxDaily) * 100}%`,
                                        minWidth: 6,
                                      }}
                                    />
                                    <span className="small fw-semibold">{d.visits}</span>
                                  </div>
                                </td>
                                <td className="text-end small text-muted">{d.newVisits}</td>
                                <td className="text-end small">{formatMoney(d.collected)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-clipboard2-pulse me-2" /> Most common diagnoses
                </Card.Header>
                {diagnoses.length === 0 ? (
                  <EmptyState icon="bi-clipboard" title="Nothing recorded yet" />
                ) : (
                  <ListGroup variant="flush">
                    {diagnoses.map((d) => (
                      <ListGroup.Item key={d.name} className="d-flex justify-content-between py-2">
                        <span className="small">{d.name}</span>
                        <span className="fw-semibold small">{d.count}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-capsule me-2" /> Most prescribed medicines
                </Card.Header>
                {medicines.length === 0 ? (
                  <EmptyState icon="bi-capsule" title="Nothing recorded yet" />
                ) : (
                  <ListGroup variant="flush">
                    {medicines.map((m) => (
                      <ListGroup.Item key={m.name} className="d-flex justify-content-between py-2">
                        <span className="small">{m.name}</span>
                        <span className="fw-semibold small">{m.count}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card>
            </Col>

            <Col xs={12}>
              <Card>
                <Card.Header>
                  <i className="bi bi-calendar-check me-2" /> Follow-ups due in the next 14 days
                </Card.Header>
                {followUps.length === 0 ? (
                  <EmptyState icon="bi-calendar" title="No follow-ups scheduled" />
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Due Date</th>
                          <th>Patient</th>
                          <th>Mobile</th>
                          <th>For</th>
                        </tr>
                      </thead>
                      <tbody>
                        {followUps.map((f) => (
                          <tr
                            key={f._id}
                            className="queue-row"
                            onClick={() => f.patient && navigate(`/patients/${f.patient._id}`)}
                          >
                            <td className="small fw-semibold">{formatDate(f.followUpDate)}</td>
                            <td>{f.patient?.name || '-'}</td>
                            <td className="small">{f.patient?.mobile || '-'}</td>
                            <td className="small text-muted">{f.diagnosis?.join(', ') || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
